/**
 * Command Palette · ⌘K 全局快捷面板
 *
 * 基于 cmdk 实现的 Folo 风格命令面板。
 * - 唤起：⌘K / Ctrl+K
 * - 命令分组：导航 / 操作 / 设置
 * - 支持模糊搜索 + 键盘导航
 *
 * 使用：在 MainLayout 顶层渲染 <CommandPalette />，全局生效。
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import { motion, AnimatePresence } from 'framer-motion';
import { Box, Typography, useTheme } from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import LabelIcon from '@mui/icons-material/Label';
import TerminalIcon from '@mui/icons-material/Terminal';
import { commandPaletteVariants } from '../../theme/motion';
import { accent, zinc, radius, shadow } from '../../theme/tokens';

interface CommandDef {
  id: string;
  label: string;
  hint?: string;
  keywords?: string;
  shortcut?: string[];
  icon?: React.ReactNode;
  perform: () => void;
  group: 'Navigation' | 'Settings' | 'Actions';
}

export const CommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  // 全局 ⌘K / Ctrl+K 监听
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setSearch('');
  }, []);

  const run = useCallback(
    (perform: () => void) => {
      perform();
      close();
    },
    [close],
  );

  const commands = useMemo<CommandDef[]>(
    () => [
      // Navigation
      {
        id: 'nav.articles',
        label: '文章',
        hint: 'Articles · 阅读未读',
        keywords: 'articles inbox feed reading',
        icon: <ArticleIcon fontSize="small" />,
        perform: () => navigate('/articles'),
        group: 'Navigation',
      },
      {
        id: 'nav.subscriptions',
        label: '订阅源',
        hint: 'Subscriptions · 管理 RSS / 公众号',
        keywords: 'subscriptions rss feed wechat',
        icon: <RssFeedIcon fontSize="small" />,
        perform: () => navigate('/subscriptions'),
        group: 'Navigation',
      },
      {
        id: 'nav.tags',
        label: '标签',
        hint: 'Tags · 浏览全部标签',
        keywords: 'tags labels',
        icon: <LabelIcon fontSize="small" />,
        perform: () => navigate('/tags'),
        group: 'Navigation',
      },
      {
        id: 'nav.stats',
        label: '统计',
        hint: 'Reading stats · 阅读数据',
        keywords: 'stats statistics',
        icon: <BarChartIcon fontSize="small" />,
        perform: () => navigate('/stats'),
        group: 'Navigation',
      },
      {
        id: 'nav.starred',
        label: '收藏文章',
        hint: 'Starred · 我的星标',
        keywords: 'starred favorites bookmark',
        icon: <BookmarkIcon fontSize="small" />,
        perform: () => navigate('/articles?favorite=true'),
        group: 'Navigation',
      },
      // Settings
      {
        id: 'set.api-tokens',
        label: 'API 令牌',
        hint: 'Agent Token · 给 hermes/openclaw 复制 Prompt',
        keywords: 'api token agent hermes openclaw',
        icon: <VpnKeyIcon fontSize="small" />,
        shortcut: ['G', 'T'],
        perform: () => navigate('/settings/api-tokens'),
        group: 'Settings',
      },
      {
        id: 'set.main',
        label: '设置',
        hint: 'Settings · 偏好与集成',
        keywords: 'settings preferences',
        icon: <SettingsIcon fontSize="small" />,
        perform: () => navigate('/settings'),
        group: 'Settings',
      },
      {
        id: 'set.notifications',
        label: '通知设置',
        hint: 'Notifications · 飞书 / Webhook',
        keywords: 'notification feishu webhook',
        icon: <SettingsIcon fontSize="small" />,
        perform: () => navigate('/settings/notifications'),
        group: 'Settings',
      },
      // Actions
      {
        id: 'act.copy-token-prompt',
        label: '复制最近 Token 的 Agent Prompt',
        hint: '快速跳到 API Tokens 页',
        keywords: 'copy prompt agent',
        icon: <TerminalIcon fontSize="small" />,
        perform: () => navigate('/settings/api-tokens'),
        group: 'Actions',
      },
    ],
    [navigate],
  );

  const groups: Array<CommandDef['group']> = ['Navigation', 'Settings', 'Actions'];
  const groupNames: Record<CommandDef['group'], string> = {
    Navigation: '导航',
    Settings: '设置',
    Actions: '操作',
  };

  return (
    <AnimatePresence>
      {open && (
        <Box
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          onClick={close}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 110,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '15vh',
            backgroundColor: isDark ? 'rgba(9,9,11,0.65)' : 'rgba(0,0,0,0.45)',
            backdropFilter: 'blur(6px)',
          }}
        >
          <motion.div
            variants={commandPaletteVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
            style={{ width: 'min(640px, 92vw)' }}
          >
            <Command
              label="Global Command Menu"
              shouldFilter={true}
              filter={(value, search, keywords) => {
                const haystack = `${value} ${(keywords ?? []).join(' ')}`.toLowerCase();
                return haystack.includes(search.toLowerCase()) ? 1 : 0;
              }}
              style={{
                backgroundColor: isDark ? zinc[900] : '#ffffff',
                border: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
                borderRadius: radius.xl,
                boxShadow: shadow.xl,
                overflow: 'hidden',
              }}
            >
              <Command.Input
                value={search}
                onValueChange={setSearch}
                placeholder="搜索命令、订阅、跳转…"
                style={{
                  width: '100%',
                  border: 'none',
                  borderBottom: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
                  outline: 'none',
                  background: 'transparent',
                  padding: '14px 18px',
                  fontSize: 15,
                  color: isDark ? zinc[50] : zinc[900],
                  fontFamily: 'inherit',
                }}
              />
              <Command.List
                style={{
                  maxHeight: 420,
                  overflowY: 'auto',
                  padding: 6,
                }}
              >
                <Command.Empty>
                  <Box sx={{ p: 4, textAlign: 'center', color: isDark ? zinc[500] : zinc[500] }}>
                    <Typography variant="body2">没有匹配的命令</Typography>
                  </Box>
                </Command.Empty>

                {groups.map((g) => (
                  <Command.Group
                    key={g}
                    heading={
                      <Box
                        sx={{
                          px: 1.5,
                          pt: 1.5,
                          pb: 0.5,
                          fontSize: 11,
                          fontWeight: 600,
                          letterSpacing: '0.05em',
                          textTransform: 'uppercase',
                          color: isDark ? zinc[500] : zinc[500],
                        }}
                      >
                        {groupNames[g]}
                      </Box>
                    }
                  >
                    {commands
                      .filter((c) => c.group === g)
                      .map((cmd) => (
                        <Command.Item
                          key={cmd.id}
                          value={`${cmd.label} ${cmd.hint ?? ''}`}
                          keywords={cmd.keywords ? cmd.keywords.split(' ') : []}
                          onSelect={() => run(cmd.perform)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 12px',
                            margin: '2px 0',
                            borderRadius: 8,
                            cursor: 'pointer',
                            color: isDark ? zinc[200] : zinc[800],
                            fontSize: 14,
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 1,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {cmd.icon}
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 500,
                                color: theme.palette.text.primary,
                                lineHeight: 1.3,
                              }}
                            >
                              {cmd.label}
                            </Typography>
                            {cmd.hint && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: isDark ? zinc[500] : zinc[500],
                                  fontSize: 11,
                                }}
                              >
                                {cmd.hint}
                              </Typography>
                            )}
                          </Box>
                          {cmd.shortcut && (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                color: isDark ? zinc[500] : zinc[500],
                                fontSize: 11,
                              }}
                            >
                              {cmd.shortcut.map((s) => (
                                <Box
                                  key={s}
                                  component="kbd"
                                  sx={{
                                    padding: '2px 6px',
                                    borderRadius: 0.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    fontFamily: 'JetBrains Mono, monospace',
                                    fontSize: 10,
                                  }}
                                >
                                  {s}
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Command.Item>
                      ))}
                  </Command.Group>
                ))}
              </Command.List>

              {/* Footer hint */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 14px',
                  borderTop: `1px solid ${isDark ? zinc[800] : zinc[200]}`,
                  fontSize: 11,
                  color: isDark ? zinc[500] : zinc[500],
                }}
              >
                <Box>
                  <Box component="kbd" sx={kbdSx(isDark)}>
                    ↑↓
                  </Box>{' '}
                  选择
                  <Box component="kbd" sx={{ ...kbdSx(isDark), ml: 1.5 }}>
                    ↵
                  </Box>{' '}
                  执行
                  <Box component="kbd" sx={{ ...kbdSx(isDark), ml: 1.5 }}>
                    esc
                  </Box>{' '}
                  关闭
                </Box>
                <Box>
                  <Box component="kbd" sx={kbdSx(isDark)}>
                    ⌘K
                  </Box>
                </Box>
              </Box>

              {/* CSS for cmdk's internal selection (aria-selected) */}
              <style>{`
                [cmdk-item][aria-selected="true"] {
                  background-color: ${isDark ? 'rgba(139,92,246,0.15)' : accent[50]};
                  color: ${isDark ? accent[300] : accent[700]};
                }
                [cmdk-item][aria-selected="true"] .MuiTypography-root {
                  color: ${isDark ? accent[300] : accent[700]} !important;
                }
              `}</style>
            </Command>
          </motion.div>
        </Box>
      )}
    </AnimatePresence>
  );
};

const kbdSx = (isDark: boolean) => ({
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: 0.5,
  border: `1px solid ${isDark ? zinc[700] : zinc[300]}`,
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 10,
  lineHeight: 1.4,
  ml: 0.5,
  mr: 0.25,
});

export default CommandPalette;
